import React, { useState, useEffect, useRef } from "react";
import IconButton from "@mui/material/IconButton";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import { motion, AnimatePresence } from "framer-motion";
import { useDispatch, useSelector } from "react-redux";
import { 
  loginTherapist, 
  initiateTherapistOTP, 
  verifyTherapistOTP, 
  sendPasswordResetLink,
  clearError, 
  getCurrentTherapist,
} from "../../redux/therapistSlice"; 
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { CircularProgress } from "@mui/material";
import ForgotPasswordModal from "../signIn/ForgotPasswordModal"; // Using existing modal

const TherapistSignInForm = () => {
  const activeTab = "email";
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [countryCode, setCountryCode] = useState("+90");
  const [password, setPassword] = useState("");
  const [otpCode, setOtpCode] = useState("");
  const [rememberMe, setRememberMe] = useState(false);
  const [showForgotModal, setShowForgotModal] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  
  const dispatch = useDispatch();
  const navigate = useNavigate();
  
  // Corrected selector - using 'therapist' not 'therapists'
  const { therapist, loading, error, loginSuccess, otpSent } = useSelector((state) => state.therapist || {
    therapist: null,
    loading: false,
    error: null,
    loginSuccess: false,
    otpSent: false
  }); 

  // Function to check if therapist profile is complete
  const isProfileIncomplete = (therapistData) => {
    if (!therapistData) return true;
    
    const requiredFields = ['branchIds', 'certificates', 'education', 'expertisesTags', 'languages', 'bio'];
    
    return requiredFields.some(field => {
      const value = therapistData[field];
      // Check if field is null, undefined, empty string, or empty array
      return !value || 
             (typeof value === 'string' && value.trim() === '') ||
             (Array.isArray(value) && value.length === 0);
    });
  };

  const redirectTimeoutRef = useRef(null);
  const hasRedirectedRef = useRef(false);

  // Redirect user to dashboard or settings when login is successful
  useEffect(() => {
    if (!loginSuccess || hasRedirectedRef.current) {
      return undefined;
    }

    hasRedirectedRef.current = true;

    const handleRedirect = async () => {
      try {
        let therapistData = therapist;

        if (!therapistData) {
          const result = await dispatch(getCurrentTherapist()).unwrap();
          therapistData = result?.therapist || result;
        }

        if (therapistData && isProfileIncomplete(therapistData)) {
          toast.info(
            "Please complete your profile information. You are being redirected to settings page.",
            {
              position: "top-center",
              autoClose: 4000,
            }
          );

          redirectTimeoutRef.current = setTimeout(() => {
            navigate("/therapistSettings");
          }, 2000);
        } else {
          navigate("/therapistHomePage");
        }
      } catch (err) {
        console.error("Therapist redirect failed:", err);
        navigate("/therapistHomePage");
      }
    };

    handleRedirect();

    return () => {
      if (redirectTimeoutRef.current) {
        clearTimeout(redirectTimeoutRef.current);
      }
    };
  }, [dispatch, loginSuccess, navigate, therapist]);

  // Load remembered email
  useEffect(() => {
    const rememberedEmail = localStorage.getItem("rememberedTherapistEmail");
    if (rememberedEmail) {
      setEmail(rememberedEmail);
      setRememberMe(true);
    }
  }, []);

  // Cleanup when leaving component
  useEffect(() => {
    return () => {
      dispatch(clearError());
    };
  }, [dispatch]);

  // Handle forgot password
  const handleForgotPassword = async (email) => {
    try {
      await dispatch(sendPasswordResetLink(email)).unwrap();
      toast.success("Password reset link has been sent to your email address!");
    } catch (err) {
      toast.error(err || "Failed to send password reset link");
      throw err;
    }
  };

  const handleEmailLogin = async (e) => {
    e.preventDefault();
    dispatch(clearError());
  
    try {
      if (!email || !password) {
        toast.error("Please fill in email and password fields");
        return;
      }
      
      await dispatch(loginTherapist({ email, password })).unwrap();
      
      // If remember option is checked, save to local storage
      if (rememberMe) {
        localStorage.setItem("rememberedTherapistEmail", email);
      } else {
        localStorage.removeItem("rememberedTherapistEmail");
      }
      
      toast.success("Login successful!");
      
    } catch (err) {
      console.error("Therapist login error:", err);
      toast.error(err || "An error occurred during login");
    }
  };

  const handleInitiateOTP = async (e) => {
    e.preventDefault();
    dispatch(clearError());
  
    try {
      if (!phone || phone.length < 10) {
        toast.error("Please enter a valid phone number");
        return;
      }
      
      await dispatch(initiateTherapistOTP({ phoneNumber: phone, countryCode })).unwrap();
      toast.success("Verification code sent to your phone");
    } catch (err) {
      console.error("OTP sending error:", err);
      toast.error(err || "An error occurred while sending verification code");
    }
  };

  const handleVerifyOTP = async (e) => {
    e.preventDefault();
    dispatch(clearError());
  
    try {
      if (!otpCode || otpCode.length !== 6) {
        toast.error("Please enter the 6-digit verification code");
        return;
      }
      
      await dispatch(verifyTherapistOTP({ otpCode })).unwrap();
      
      toast.success("Login successful!");
      
    } catch (err) {
      console.error("OTP verification error:", err);
      toast.error(err || "An error occurred while verifying the code");
    }
  };

  return (
    <div className="mt-[49px] notranslate" translate="no">
      {/* Email/Phone Tabs */}
      {/* <div className="flex justify-between items-center bg-[#FFFFFF] rounded-[30px] h-[37px] px-2 py-1 mb-4">
        <button
          type="button"
          onClick={() => setActiveTab("email")}
          className={`w-1/2 h-[26px] text-center font-poppins text-[20px] rounded-full ${
            activeTab === "email" ? "bg-button-bg text-white" : "text-text"
          }`}
        >
          Email
        </button>
        <button
          type="button"
          onClick={() => setActiveTab("phone")}
          className={`w-1/2 h-[26px] text-center font-poppins text-[20px] rounded-full ${
            activeTab === "phone" ? "bg-button-bg text-white" : "text-text"
          }`}
        >
          Phone
        </button>
      </div> */}

      {/* Instruction Text */}
      <p className="text-text font-poppins text-[20px] ml-4 mb-4">
        <span>Login as a Therapist</span>
      </p>

      {/* Login Form */}
      <AnimatePresence mode="wait">
        {activeTab === "email" && (
          <motion.form
            key="email-form"
            onSubmit={handleEmailLogin}
            initial={{ opacity: 0, x: "-10%" }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: "10%" }}
            transition={{ duration: 0.3 }}
          >
            {/* Email Input */}
            <input
              type="email"
              placeholder="Email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="w-full px-4 py-2 border-2 border-cards-border focus:ring-2 focus:ring-[#1B5E20] rounded-tr-[30px] rounded-tl-[30px]"
            />

            {/* Password Input */}
            <div className="relative">
              <input
                type={showPassword ? "text" : "password"}
                placeholder="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="w-full px-4 py-2 pr-12 border-2 border-cards-border rounded-bl-[30px] rounded-br-[30px] focus:outline-none focus:ring-2 focus:ring-[#1B5E20]"
              />
              <IconButton
                onClick={() => setShowPassword((prev) => !prev)}
                className="!absolute right-8 top-1/2 -translate-y-1/2"
                edge="end"
                size="small"
              >
                {showPassword ? <VisibilityOff /> : <Visibility />}
              </IconButton>
            </div>

            {/* Checkbox Area */}
            <div className="flex items-center border-cards-border mt-4 ml-4">
              <input
                type="checkbox"
                id="remember"
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
                className="w-6 h-6 text-text border-[3px] border-cards-border rounded-full"
              />
              <label
                htmlFor="remember"
                className="ml-2 text-[20px] font-poppins text-text"
              >
                <span>Remember me</span>
              </label>
            </div>

            {/* Forgot Password Link */}
            <div className="text-right mt-3">
              <button
                type="button"
                onClick={() => setShowForgotModal(true)}
                className="text-text hover:text-text-dark text-sm font-poppins"
              >
                <span>Forgot Password?</span>
              </button>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              className={`w-full h-[61px] ${
                loading ? "bg-gray-400" : "bg-button-bg"
              } text-white py-2 rounded-full text-[20px] font-poppins mt-6 flex justify-center items-center`}
              disabled={loading}
            >
              {loading ? (
                <>
                  <CircularProgress size={24} color="inherit" className="mr-2" />
                  <span>Logging in...</span>
                </>
              ) : (
                <span>Sign In</span>
              )}
            </button>
          </motion.form>
        )}

        {activeTab === "phone" && !otpSent && (
          <motion.form
            key="phone-form"
            onSubmit={handleInitiateOTP}
            initial={{ opacity: 0, x: "10%" }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: "-10%" }}
            transition={{ duration: 0.3 }}
          >
            {/* Phone Input with Country Code */}
            <div className="flex items-center border-2 border-cards-border rounded-[30px]">
              {/* Country Code Dropdown */}
              <select
                className="px-4 py-2 text-[16px] text-text border-r-2 border-cards-border font-poppins outline-none focus:ring-2 focus:ring-[#1B5E20] rounded-tl-[30px] rounded-bl-[30px]"
                value={countryCode}
                onChange={(e) => setCountryCode(e.target.value)}
              >
                <option value="+90">+90 (Turkey)</option>
                <option value="+1">+1 (US/Canada)</option>
                <option value="+44">+44 (UK)</option>
                <option value="+49">+49 (Germany)</option>
              </select>

              {/* Phone Number Input */}
              <input
                type="tel"
                placeholder="Phone Number"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                required
                pattern="[0-9]{10}"
                className="w-full px-4 py-2 bg-white text-[16px] rounded-tr-[30px] rounded-br-[30px] focus:outline-none focus:ring-2 focus:ring-[#1B5E20]"
              />
            </div>

            {/* OTP verification code sending button */}
            <button
              type="submit"
              className={`w-full h-[61px] ${
                loading ? "bg-gray-400" : "bg-button-bg"
              } text-white py-2 rounded-full text-[20px] font-poppins mt-6 flex justify-center items-center`}
              disabled={loading}
            >
              {loading ? (
                <>
                  <CircularProgress size={24} color="inherit" className="mr-2" />
                  Sending...
                </>
              ) : (
                "Send Verification Code"
              )}
            </button>
          </motion.form>
        )}

        {activeTab === "phone" && otpSent && (
          <motion.form
            key="otp-form"
            onSubmit={handleVerifyOTP}
            initial={{ opacity: 0, y: "10%" }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: "-10%" }}
            transition={{ duration: 0.3 }}
          >
            {/* OTP Input */}
            <div className="mb-4">
              <label className="block text-text font-poppins text-[16px] mb-2">
                Enter Verification Code
              </label>
              <input
                type="text"
                placeholder="6-digit verification code"
                value={otpCode}
                onChange={(e) => setOtpCode(e.target.value)}
                required
                maxLength="6"
                pattern="[0-9]{6}"
                className="w-full px-4 py-2 border-2 border-cards-border rounded-[30px] focus:outline-none focus:ring-2 focus:ring-[#1B5E20]"
              />
            </div>

            {/* OTP verification button */}
            <button
              type="submit"
              className={`w-full h-[61px] ${
                loading ? "bg-gray-400" : "bg-button-bg"
              } text-white py-2 rounded-full text-[20px] font-poppins mt-6 flex justify-center items-center`}
              disabled={loading}
            >
              {loading ? (
                <>
                  <CircularProgress size={24} color="inherit" className="mr-2" />
                  Verifying...
                </>
              ) : (
                "Verify & Login"
              )}
            </button>

            {/* Resend Code Button */}
            <button
              type="button"
              onClick={handleInitiateOTP}
              className="w-full py-2 text-text hover:text-text-dark text-[14px] font-poppins transition-colors mt-2"
            >
              Resend Verification Code
            </button>
          </motion.form>
        )}
      </AnimatePresence>

      {/* Error Message */}
      {error && (
        <div className="text-red-500 text-center mt-4 p-2 bg-red-50 rounded-md font-poppins">
          {error}
        </div>
      )}

      {/* Using existing ForgotPasswordModal */}
      <ForgotPasswordModal
        isOpen={showForgotModal}
        onClose={() => setShowForgotModal(false)}
        onSend={handleForgotPassword}
      />
    </div>
  );
};

export default TherapistSignInForm;
